/* eslint-disable  @typescript-eslint/naming-convention */
import { Directive, Input, OnDestroy, OnInit, Inject } from '@angular/core';

import { merge } from 'lodash-es';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EditorComponent } from '@tinymce/tinymce-angular';

import { ENV_TOKEN, IEnvironment } from 'sc-common/core/models/environment';

import { ApiText } from '../../core/services/open-api/open-api-clients';
import { TextClient } from '../../core/services/open-api/shared-api-client';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'editor',
    standalone: false
})
export class TinyMceEditorDirective implements OnInit, OnDestroy {

    @Input()
    public placeholder: string;

    @Input()
    public richEditor = false;

    @Input()
    public formattingTagsOnly = false;

    // Inline content mode (titles, labels).
    // The outer <p> added by TinyMCE is removed when storing the value.
    @Input()
    public inlineContent = true;

    private readonly _destroy$ = new Subject<void>();

    // Becomes true once the editor has finished its initial content load.
    // Until then, the change callback is suppressed so that the HTML
    // normalization TinyMCE performs on init does not mark the form dirty.
    private _initialized = false;

    constructor(
        private readonly _editorComponent: EditorComponent,
        private readonly _textClient: TextClient,
        @Inject(ENV_TOKEN) private readonly _env: IEnvironment
    ) {
        // Gate the ControlValueAccessor change callback behind the
        // initialization flag. This must be wired up in the constructor,
        // before Angular's form directive calls registerOnChange.
        const originalRegisterOnChange = this._editorComponent.registerOnChange.bind(this._editorComponent);
        this._editorComponent.registerOnChange = (fn: (_: any) => void) => {
            originalRegisterOnChange((value: any) => {
                if (this._initialized) {
                    fn(value);
                }
            });
        };
    }

    public ngOnInit(): void {
        const defaultOptions: Record<string, any> = {
            menubar: false,
            statusbar: false,
            resize: false,
            height: 200,
            branding: false,
            placeholder: this.placeholder,
            plugins: 'code charmap',
            toolbar: 'code source italic subscript superscript capitalize charmap',
            formats: {
                bold: { inline: 'b' },
                italic: { inline: 'i' }
            },
            extended_valid_elements: 'b/strong,i/em',
            element_format: 'xhtml',
            entity_encoding: 'named',
            setup: (editor: any) => {
                editor.ui.registry.addButton('capitalize', {
                    icon: 'change-case',
                    tooltip: $localize`Clean & Capitalize`,
                    onAction: () => {
                        this._textClient.cleanAndCapitalize(new ApiText({ text: editor.getContent() }))
                            .pipe(takeUntil(this._destroy$))
                            .subscribe(capitalized => {
                                editor.setContent(capitalized);
                                editor.fire('change');
                            });
                    }
                });

                if (this.inlineContent) {
                    editor.on('GetContent', (e: any) => {
                        e.content = this.unwrapFromTinyMce(e.content);
                    });
                }
            }
        };

        let options;

        if (this.formattingTagsOnly) {
            const minTagOptions: Record<string, any> =
            {
                valid_elements: 'sup,i,sub',
                invalid_elements: "strong,b"
            }

            options = merge({}, defaultOptions, minTagOptions);
        }

        if (this.richEditor) {

            const richOptions: Record<string, any> = {
                menubar: 'file edit view insert format tools table help',
                statusbar: true,
                resize: true,
                placeholder: this.placeholder,
                plugins: 'preview importcss searchreplace autolink autosave save code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help quickbars emoticons template',
                toolbar: 'undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen preview save | insertfile image media template link anchor codesample',
                toolbar_sticky: true,
                fontsize_formats: '6pt 8pt 10pt 12pt 14pt 18pt 24pt 36pt',
                image_advtab: true,
                importcss_append: true,
                image_caption: true,
                quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
                noneditable_noneditable_class: 'mceNonEditable',
                ncu: 'sliding',
                contextmenu: 'link image table'
            };

            options = merge({}, defaultOptions, richOptions);
        }

        if (!this.formattingTagsOnly && !this.richEditor) {
            options = defaultOptions;
        }

        this._editorComponent.init = options;

        if (this.inlineContent) {
            const originalWriteValue = this._editorComponent.writeValue.bind(this._editorComponent);
            this._editorComponent.writeValue = (value: any) => {
                originalWriteValue(this.wrapForTinyMce(value));
            };
        }

        // The wrapper's onInit fires after its internal initEditor() has run
        // (including the normalization-driven change on initial content load).
        // Flipping the flag here lets only genuine user edits mark the form dirty.
        this._editorComponent.onInit
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this._initialized = true);
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private wrapForTinyMce(value: string): string {
        if (!value) return value;
        if (/^\s*<(p|div|h[1-6]|ul|ol|table|blockquote|pre)[\s>]/i.test(value)) return value;
        return `<p>${ value }</p>`;
    }

    private unwrapFromTinyMce(html: string): string {
        if (!html) return html;
        const trimmed = html.trim();
        const match = trimmed.match(/^<p>([\s\S]*)<\/p>$/);
        if (!match) return html;
        // Don't unwrap if there are multiple paragraphs
        if (match[1].includes('</p>')) return html;
        return match[1];
    }
}
